import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, SlidersHorizontal, Check, Plus, Minus, AlertTriangle } from 'lucide-react';
import { Button } from './SubscriptionAppButton';
import { Checkbox } from './Checkbox';
// Types
export interface Product {
  id: string;
  name: string;
  subtitle: string;
  price: number;
  stock: number | null; // null when inventory isn't tracked
  status: 'In Stock' | 'Low Stock' | 'Out of Stock' | 'Inventory Not Tracked';
  color: string; // for the bottle placeholder
  sku?: string;
  isBundle?: boolean;
}
export interface ProductSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedIds: string[], quantities?: Record<string, number>) => void;
  mode: 'custom' | 'curated';
  packMin?: number; // Required for curated mode validation
  packMax?: number; // Required for curated mode validation
  // Optional product list; defaults to the Club Builder catalogue
  products?: Product[];
  // Optional pre-checked products (custom mode); overrides the default selection
  initialSelectedIds?: string[];
  // Optional starting quantities (curated mode); overrides the default selection
  initialQuantities?: Record<string, number>;
}
const STATUS_BADGE_STYLES: Record<Product['status'], string> = {
  'In Stock': 'bg-[#D1FAE5] text-[#059669]',
  'Low Stock': 'bg-[#FEF3C7] text-[#D97706]',
  'Out of Stock': 'bg-[#FEE2E2] text-[#DC2626]',
  'Inventory Not Tracked': 'bg-[#F1F5F9] text-[#475569]'
};
// Club Builder catalogue
export const CLUB_BUILDER_PRODUCTS: Product[] = [{
  id: '1',
  name: '2022 Reserve Shiraz',
  subtitle: '750ml · Red Wine',
  price: 48.0,
  stock: 156,
  status: 'In Stock',
  color: '#FECACA' // Red-200
}, {
  id: '2',
  name: '2021 Chardonnay Reserve',
  subtitle: '750ml · White Wine',
  price: 42.0,
  stock: 8,
  status: 'Low Stock',
  color: '#FEF3C7' // Amber-100
}, {
  id: '3',
  name: 'Cabernet Sauvignon 2019',
  subtitle: '750ml · Red Wine',
  price: 58.0,
  stock: 89,
  status: 'In Stock',
  color: '#FECACA'
}, {
  id: '4',
  name: 'Sauvignon Blanc 2023',
  subtitle: '750ml · White Wine',
  price: 32.0,
  stock: 243,
  status: 'In Stock',
  color: '#FEF3C7'
}, {
  id: '5',
  name: 'Rosé 2023',
  subtitle: '750ml · Rosé',
  price: 28.0,
  stock: 178,
  status: 'In Stock',
  color: '#FCE7F3' // Pink-100
}, {
  id: '6',
  name: 'Merlot 2021',
  subtitle: '750ml · Red Wine',
  price: 38.0,
  stock: 67,
  status: 'In Stock',
  color: '#FECACA'
}, {
  id: '7',
  name: 'Riesling 2023',
  subtitle: '750ml · White Wine',
  price: 30.0,
  stock: 134,
  status: 'In Stock',
  color: '#FEF3C7'
}, {
  id: '8',
  name: 'Pinot Noir Estate 2020',
  subtitle: '750ml · Red Wine',
  price: 55.0,
  stock: 0,
  status: 'Out of Stock',
  color: '#FECACA'
}, {
  id: '9',
  name: 'Tempranillo 2020',
  subtitle: '750ml · Red Wine',
  price: 44.0,
  stock: 23,
  status: 'In Stock',
  color: '#FECACA'
}, {
  id: '10',
  name: 'GSM Blend 2021',
  subtitle: '750ml · Red Wine',
  price: 36.0,
  stock: 91,
  status: 'In Stock',
  color: '#FECACA'
}, {
  id: 'pal-1',
  name: '2024 Palisander Fiano',
  subtitle: '750ml · White Wine',
  price: 36.0,
  stock: 210,
  status: 'In Stock',
  color: '#FEF3C7'
}, {
  id: 'pal-2',
  name: '2024 Palisander Rosé',
  subtitle: '750ml · Rosé',
  price: 34.0,
  stock: 164,
  status: 'In Stock',
  color: '#FCE7F3'
}, {
  id: 'pal-3',
  name: '2022 Palisander Shiraz',
  subtitle: '750ml · Red Wine',
  price: 48.0,
  stock: 132,
  status: 'In Stock',
  color: '#FECACA'
}, {
  id: 'pal-4',
  name: '2021 Palisander Reserve Cabernet',
  subtitle: '750ml · Red Wine',
  price: 78.0,
  stock: 58,
  status: 'In Stock',
  color: '#FECACA'
}, {
  id: 'pal-5',
  name: '2019 Palisander Museum Shiraz',
  subtitle: '750ml · Red Wine',
  price: 96.0,
  stock: 14,
  status: 'Low Stock',
  color: '#FECACA'
}];
export function ProductSourceModal({
  isOpen,
  onClose,
  onConfirm,
  mode,
  packMin = 0,
  packMax = 0,
  products,
  initialSelectedIds,
  initialQuantities
}: ProductSourceModalProps) {
  const catalogue = products ?? CLUB_BUILDER_PRODUCTS;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  // Initialize default selections based on mode
  useEffect(() => {
    if (isOpen) {
      if (mode === 'curated' && initialQuantities) {
        setSelectedIds(new Set(Object.keys(initialQuantities)));
        setQuantities({
          ...initialQuantities
        });
      } else if (mode === 'curated') {
        // Default curated selection: first 6 items
        // Shiraz: 2, Chardonnay: 2, Cabernet: 2, Sauv Blanc: 1, Rosé: 1, Merlot: 2 => Total 10
        const initialSelected = new Set(['1', '2', '3', '4', '5', '6']);
        const initialQuantities = {
          '1': 2,
          '2': 2,
          '3': 2,
          '4': 1,
          '5': 1,
          '6': 2
        };
        setSelectedIds(initialSelected);
        setQuantities(initialQuantities);
      } else {
        // Default custom selection: everything except Out of Stock (id 8) and one other (id 9)
        const initialSelected = new Set(initialSelectedIds ?? ['1', '2', '3', '4', '5', '6', '7', '10']);
        setSelectedIds(initialSelected);
        setQuantities({});
      }
      setSearchQuery('');
    }
  }, [isOpen, mode]);
  // Filter products by name or SKU
  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return catalogue;
    return catalogue.filter(p => p.name.toLowerCase().includes(query) || (p.sku ?? '').toLowerCase().includes(query));
  }, [searchQuery, catalogue]);
  // Calculations
  const totalBottlesSelected = useMemo(() => {
    return Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  }, [quantities]);
  const estimatedTotal = useMemo(() => {
    return Array.from(selectedIds).reduce((sum, id) => {
      const product = catalogue.find(p => p.id === id);
      const qty = quantities[id] || (mode === 'curated' ? 0 : 1);
      if (mode === 'curated') {
        return sum + (product?.price || 0) * qty;
      }
      return sum;
    }, 0);
  }, [selectedIds, quantities, mode]);
  // Handlers
  const toggleSelection = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
      if (mode === 'curated') {
        const newQuantities = {
          ...quantities
        };
        delete newQuantities[id];
        setQuantities(newQuantities);
      }
    } else {
      newSet.add(id);
      if (mode === 'curated') {
        setQuantities(prev => ({
          ...prev,
          [id]: 1
        }));
      }
    }
    setSelectedIds(newSet);
  };
  const updateQuantity = (id: string, delta: number) => {
    const currentQty = quantities[id] || 0;
    const newQty = Math.max(1, currentQty + delta);
    setQuantities(prev => ({
      ...prev,
      [id]: newQty
    }));
  };
  // Select All logic
  const allFilteredSelected = filteredProducts.length > 0 && filteredProducts.every(p => selectedIds.has(p.id));
  const someFilteredSelected = filteredProducts.some(p => selectedIds.has(p.id)) && !allFilteredSelected;
  const toggleSelectAll = () => {
    const newSet = new Set(selectedIds);
    const newQuantities = {
      ...quantities
    };
    if (allFilteredSelected) {
      // Deselect all filtered products
      filteredProducts.forEach(p => {
        newSet.delete(p.id);
        if (mode === 'curated') {
          delete newQuantities[p.id];
        }
      });
    } else {
      // Select all filtered products
      filteredProducts.forEach(p => {
        if (!newSet.has(p.id)) {
          newSet.add(p.id);
          if (mode === 'curated') {
            newQuantities[p.id] = 1;
          }
        }
      });
    }
    setSelectedIds(newSet);
    if (mode === 'curated') {
      setQuantities(newQuantities);
    }
  };
  const handleConfirm = () => {
    onConfirm(Array.from(selectedIds), mode === 'curated' ? quantities : undefined);
    onClose();
  };
  if (!isOpen) return null;
  const isOverLimit = mode === 'curated' && totalBottlesSelected > packMax;
  const isUnderLimit = mode === 'curated' && totalBottlesSelected < packMin;
  const isConfirmDisabled = mode === 'curated' && isUnderLimit;
  return <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4" data-id="element-4387" data-mp="mksao5e427xn">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0F172A]/40 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-4388" data-mp="rb8o3phxltk0" />


      {/* Modal Container */}
      <div className="relative w-full max-w-[900px] max-h-[85vh] bg-white rounded-xl shadow-xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-4389" data-mp="cnxbrldxe5iu">
        {/* Header */}
        <div className="flex-none px-6 py-5 border-b border-[#E2E8F0] flex items-start justify-between bg-white" data-id="element-4390" data-mp="3mz6kzrr3ryo">
          <div data-id="element-4391" data-mp="7m83gdi385yg">
            <h2 className="text-[16px] font-bold text-[#0F172A]" data-id="element-4392" data-mp="7mq37k3yl2b4">
              Select Products
            </h2>
            <div className="mt-1 flex items-center gap-2" data-id="element-4393" data-mp="3l6g7lm0codga">
              {mode === 'curated' ? <div className={`flex items-center gap-2 text-[13px] font-medium ${isOverLimit ? 'text-[#EF4444]' : isUnderLimit ? 'text-[#F59E0B]' : 'text-[#10B981]'}`} data-id="element-4394" data-mp="3g7d1dy8mw16q">

                  {isOverLimit || isUnderLimit ? <AlertTriangle className="w-4 h-4" data-id="element-4395" data-mp="3p4vt1lgs9drl" /> : <Check className="w-4 h-4" data-id="element-4396" data-mp="7xtiu9bcdbie" />}
                  {totalBottlesSelected} of {packMax} bottles assigned
                  {isOverLimit && <span className="text-[#EF4444] ml-1" data-id="element-4397" data-mp="2y7aad6gkjfx">(Over limit)</span>}
                  {isUnderLimit && <span className="text-[#F59E0B] ml-1" data-id="element-4398" data-mp="gpc3oxj8gfht">
                      (Minimum {packMin})
                    </span>}
                </div> : <p className="text-[14px] text-[#64748B]" data-id="element-4399" data-mp="cqwhuwyw1d5z">
                  {selectedIds.size} of {catalogue.length} products selected
                </p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded transition-colors" data-id="element-4400" data-mp="lrb541vex2c4">

            <X className="w-5 h-5" data-id="element-4401" data-mp="2d0vz4sb26z92" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto bg-[#F8FAFC]" data-id="element-4402" data-mp="28z124005v813">
          <div className="p-6 space-y-6" data-id="element-4403" data-mp="28ziuumnm7449">
            {/* Search & Filter */}
            <div className="flex gap-3" data-id="element-4404" data-mp="241ha7xajiwqo">
              <div className="relative flex-1" data-id="element-4405" data-mp="30qgmdne8wio5">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" data-id="element-4406" data-mp="30p5izooppcxk" />
                <input type="text" placeholder="Search products by name or SKU..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full h-10 pl-10 pr-4 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20" data-id="element-4407" data-mp="2wq6hyf63q0be" />

              </div>
              <Button variant="outline" iconLeft={<SlidersHorizontal className="w-4 h-4" data-id="element-4409" data-mp="1bazdde0etrlp" />} data-id="element-4408" data-mp="1g8ipo8djrub0">

                Filter
              </Button>
            </div>

            {/* Product Table */}
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden shadow-sm" data-id="element-4410" data-mp="115h9lfktftir">
              <table className="w-full" data-id="element-4411" data-mp="164kg74gg27su">
                <thead data-id="element-4412" data-mp="1b5843h9dm8iu">
                  <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]" data-id="element-4413" data-mp="1b4zrzrl2v78p">
                    <th className="w-12 py-3 pl-4" data-id="element-4414" data-mp="1fwzunrsj8e8z">
                      {mode === 'custom' && <Checkbox checked={allFilteredSelected} indeterminate={someFilteredSelected} onChange={toggleSelectAll} data-id="element-4415" data-mp="1jy3bohmbggbs" />}
                    </th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4416" data-mp="1ovv73lm66kkq">
                      Product
                    </th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4417" data-mp="1yvm0tl2wovjr">
                      Price
                    </th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4418" data-mp="4h1hbbqbn61y">
                      Stock
                    </th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4419" data-mp="3snufpp2yjsh9">
                      Status
                    </th>
                    {mode === 'curated' && <th className="text-right py-3 px-4 text-[11px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4420" data-mp="6cgficl2purq">
                        Quantity
                      </th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-4421" data-mp="agxxc67vhxzv">
                  {filteredProducts.map(product => {
                  const isSelected = selectedIds.has(product.id);
                  const quantity = quantities[product.id] || 0;
                  const lineTotal = quantity * product.price;
                  return <tr key={product.id} className={`transition-colors ${isSelected && mode === 'curated' ? 'bg-[#F0F9FF]' : 'hover:bg-[#F8FAFC]'}`} data-id="element-4422" data-mp="y57bqn2rbjgz">

                        <td className="py-3 pl-4 align-middle" data-id="element-4423" data-mp="u2kjil26bwj9">
                          <Checkbox checked={isSelected} onChange={() => toggleSelection(product.id)} data-id="element-4424" data-mp="1ct7fepud96f0" />

                        </td>
                        <td className="py-3 px-4 align-middle" data-id="element-4425" data-mp="1hpem4kul6ly0">
                          <div className="flex items-center gap-3" data-id="element-4426" data-mp="13rhorm8b6bia">
                            <div className="w-8 h-10 rounded flex-shrink-0 flex items-center justify-center" style={{
                          backgroundColor: product.color
                        }} data-id="element-4427" data-mp="18qb3tj38uakk">

                              <div className="w-3 h-6 border-2 border-white/40 rounded-full opacity-50" data-id="element-4428" data-mp="yt6cl4ujjoww" />
                            </div>
                            <div data-id="element-4429" data-mp="l3cpos2ljept">
                              <div className="text-[14px] font-bold text-[#0F172A] flex items-center gap-2" data-id="element-4430" data-mp="p2twbotrehwk">
                                {product.name}
                                {product.isBundle && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#E0F2FE] text-[#0369A1]" data-id="element-4431" data-mp="b5oi31jw6cq8">
                                    Bundle
                                  </span>}
                              </div>
                              <div className="text-[12px] text-[#64748B]" data-id="element-4432" data-mp="1eerjqijvw8x7">
                                {product.subtitle}
                              </div>
                              {mode === 'curated' && isSelected && <div className="text-[12px] text-[#0EA5E9] font-medium mt-0.5" data-id="element-4433" data-mp="14nwwpbytd5ap">
                                  {quantity} × ${product.price.toFixed(2)} = $
                                  {lineTotal.toFixed(2)}
                                </div>}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-[14px] font-medium text-[#0F172A] align-middle" data-id="element-4434" data-mp="lq3s858jknb7">
                          ${product.price.toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-[14px] text-[#64748B] align-middle" data-id="element-4435" data-mp="2g3v1v4qvf76n">
                          {product.stock ?? '—'}
                        </td>
                        <td className="py-3 px-4 align-middle" data-id="element-4436" data-mp="2nl06yexkg4uw">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${STATUS_BADGE_STYLES[product.status]}`} data-id="element-4437" data-mp="2skwdaktqk8dm">

                            {product.status}
                          </span>
                        </td>
                        {mode === 'curated' && <td className="py-3 px-4 align-middle text-right" data-id="element-4438" data-mp="24pjuyfaohfmp">
                            {isSelected && <div className="flex items-center justify-end gap-2" data-id="element-4439" data-mp="29oeg3nw4bg9z">
                                <button onClick={() => updateQuantity(product.id, -1)} className="w-8 h-8 flex items-center justify-center rounded border border-[#CBD5E1] bg-white text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors" data-id="element-4440" data-mp="2em6en0r9q0n5">

                                  <Minus className="w-3 h-3" data-id="element-4441" data-mp="3q3xqg8txumr3" />
                                </button>
                                <input type="number" value={quantity} onChange={e => {
                          const val = parseInt(e.target.value) || 0;
                          setQuantities(prev => ({
                            ...prev,
                            [product.id]: Math.max(1, val)
                          }));
                        }} className="w-12 h-8 text-center border border-[#CBD5E1] rounded text-[13px] focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9]" data-id="element-4442" data-mp="3u517gynq2otw" />

                                <button onClick={() => updateQuantity(product.id, 1)} className="w-8 h-8 flex items-center justify-center rounded border border-[#CBD5E1] bg-white text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors" data-id="element-4443" data-mp="17944cffiwtr1">

                                  <Plus className="w-3 h-3" data-id="element-4444" data-mp="1g87m9g2yf33y" />
                                </button>
                              </div>}
                          </td>}
                      </tr>;
                })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-none px-6 py-4 border-t border-[#E2E8F0] bg-white flex items-center justify-between" data-id="element-4445" data-mp="2z6dneqz14542">
          <div className="text-[13px] font-medium text-[#64748B]" data-id="element-4446" data-mp="2q7s8d5srn881">
            {mode === 'curated' ? <span data-id="element-4447" data-mp="2v6mofzzcmiog">
                {selectedIds.size} products selected · {totalBottlesSelected}{' '}
                bottles · Est. ${estimatedTotal.toFixed(2)}
              </span> : <span data-id="element-4448" data-mp="3iwz3rvwxbhp2">{selectedIds.size} products selected</span>}
          </div>
          <div className="flex gap-3" data-id="element-4449" data-mp="3mwhrezg4q6vl">
            <Button variant="outline" onClick={onClose} data-id="element-4450" data-mp="3mvgcukzwn294">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} disabled={isConfirmDisabled} data-id="element-4451" data-mp="390dyd9gzs4kp">

              Confirm Selection
            </Button>
          </div>
        </div>
      </div>
    </div>;
}